'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';

import FormSpinnerPage, { FormSpinnerPageProps } from '@/components/form/formSpinner/FormSpinnerPage';
import FormThemeSwitcher from '@/components/form/formThemeSwitcher/FormThemeSwitcher';
import { AuthFields, AuthWaitingStatus, AuthWaitingType } from '@/_auth/interfaces/auth.interfaces';
import { authClient } from '@/_auth/utils/auth/client';
import { Button } from '@/components/ui/button';
import { NAV_HEADER_NAME } from '@/interfaces/nav.interfaces';
import { RouteFields, RouteNames } from '@/interfaces/route.interfaces';
import { clearFormSpinner, formatErrorMessage, manageFormSpinner, manageFormSpinnerPage } from '@/lib/functions';
import { getAuthSelector } from '@/_auth/redux/auth/auth.slices';
import { useAppSelector } from '@/redux/store.hooks';
import { toast } from 'react-toastify';

import { FaMicrosoft } from 'react-icons/fa';

const SignInPage = () => {
  const [spinnerPageState, setSpinnerPageState] = useState<FormSpinnerPageProps | null>(null);
  const router = useRouter();
  const auth = useAppSelector(getAuthSelector);

  useEffect(() => {
    clearFormSpinner();
  }, []);

  useEffect(() => {
    if (auth[AuthFields.User]) {
      manageFormSpinnerPage(null, setSpinnerPageState);
      manageFormSpinner(`loading ${RouteNames[RouteFields.Dashboard].name} page - please wait`);
      router.push(RouteNames[RouteFields.Dashboard].route);
    }
  }, [auth, router]);

  const signInMicrosoft = async () => {
    manageFormSpinnerPage('redirecting to Microsoft Entra', setSpinnerPageState);

    try {
      const { error } = await authClient.auth.signInWithMicrosoft();

      if (error) {
        toast.error(formatErrorMessage(error));
        manageFormSpinnerPage(null, setSpinnerPageState);
        return;
      }

      manageFormSpinnerPage(`loading ${RouteNames[RouteFields.Waiting].name} page`, setSpinnerPageState);
      router.push(`${RouteNames[RouteFields.Waiting].route}?${AuthFields.Type}=${AuthWaitingType.Oauth_entra}&${AuthFields.Status}=${AuthWaitingStatus.Waiting}`);
    } catch (error) {
      toast.error(formatErrorMessage(error));
      manageFormSpinnerPage(null, setSpinnerPageState);
    }
  };

  return (
    <div className="relative flex h-full min-h-screen w-full flex-col justify-between bg-white px-4 py-6 dark:bg-slate-800 dark:text-white xsm:px-10 sm:h-full sm:w-full md:h-full md:w-3/5 lg:h-full lg:w-2/5 xl:h-full 3xl:px-7p 4xl:px-10p">
      <div>
        {spinnerPageState && <FormSpinnerPage isActive={spinnerPageState.isActive} caption={spinnerPageState.caption} />}
        <span onClick={() => manageFormSpinner(`loading ${RouteNames[RouteFields.Home].name} page - please wait`)}>
          <Link className="flex cursor-pointer space-x-3 whitespace-nowrap focus:outline-0" href={RouteNames[RouteFields.Home].route}>
            <Image alt="Logo" className="h-auto w-auto" height={28} src={'assets/logo-icon.svg'} width={28} />
            <span className="self-center whitespace-nowrap text-xl md:text-2xl">
              <span className="text-logoblue dark:text-logoblue-300">{NAV_HEADER_NAME.Prefix}</span>
              <span className="font-semibold">{NAV_HEADER_NAME.Suffix}</span>
            </span>
          </Link>
        </span>
        <div className="flex w-full flex-col">
          <div className="mt-[45px] w-full">
            <div className="mb-4 flex w-full flex-col">
              <h3 className="mb-2 text-2xl font-semibold">Sign In</h3>
              <p className="mb-2 text-base">Sign in with your organisation Microsoft account via Entra ID.</p>
            </div>
            <div className="my-5 flex w-full flex-col">
              <Button className="my-2 w-full bg-[#2F2F2F] text-white hover:bg-black" onClick={() => signInMicrosoft()} type="button">
                <FaMicrosoft className="mr-2" />
                Sign in with Microsoft
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="flex w-full items-center justify-between">
        <span className="text-sm font-normal text-gray-700 dark:text-gray-400">Account provisioning is managed by your administrator.</span>
        <FormThemeSwitcher />
      </div>
    </div>
  );
};

export default SignInPage;
